/**
 * Coût de revient des articles — saisi sur la page Frais, par variante Shopify.
 *
 * Une ligne par article vendu sur douze mois : coût Shopify (« coût par
 * article » de la variante, relu à chaque sync) et coût saisi ici, qui prime.
 * Vider le champ et enregistrer retire le coût saisi. Les kits Piano LED
 * vendus seuls prennent d'abord leur période de coût (bloc au-dessus) : leur
 * ligne le rappelle. Un pack « piano + Piano LED » sans coût propre se déduit :
 * kit de la période + piano vendu seul.
 */

import { SALES_CATEGORY_LABELS } from '@shared/types/sales';
import { KIT_PRODUCTION_COST_MAX, type ArticleCost } from '@shared/types/settings';
import { useEffect, useState, type FormEvent } from 'react';

import { useArticleCosts, useWriteArticleCost } from '../hooks/useArticleCosts';
import { formatDayShort, formatInteger, formatMoney } from '../lib/format';
import { Card } from './Card';

const money = (value: number) => formatMoney(value, 'EUR', true);

function parseCost(raw: string): number | null | 'invalid' {
  const cleaned = raw.replace(/\s/g, '').replace('€', '').replace(',', '.');
  if (cleaned === '') return null;
  const value = Number(cleaned);
  return Number.isFinite(value) && value >= 0 && value <= KIT_PRODUCTION_COST_MAX ? value : 'invalid';
}

function ArticleRow({ article, isSaving, onSave }: { article: ArticleCost; isSaving: boolean; onSave: (unitCost: number | null) => Promise<void> }) {
  const stored = article.unitCost === null ? '' : String(article.unitCost).replace('.', ',');
  const [draft, setDraft] = useState(stored);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setDraft(stored), [stored]);

  const parsed = parseCost(draft);
  const dirty = draft !== stored;
  const effective = article.unitCost ?? article.shopifyCost ?? article.derivedCost;
  const derived = article.unitCost === null && article.shopifyCost === null && article.derivedCost !== null;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (parsed === 'invalid') return setError(`Un nombre entre 0 et ${KIT_PRODUCTION_COST_MAX} €.`);
    setError(null);
    try {
      await onSave(parsed);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Enregistrement impossible.');
    }
  };

  return (
    <tr>
      <td className="wrap">
        {article.title}
        {article.variantTitle && <span className="kpi__hint"> — {article.variantTitle}</span>}
        {article.category === 'kit' && <span className="kpi__hint"> · vendu seul : coût de la période</span>}
      </td>
      <td>{SALES_CATEGORY_LABELS[article.category]}</td>
      <td className="num">{formatInteger(article.unitsLast12Months)}</td>
      <td>{formatDayShort(article.lastSoldAt.slice(0, 10))}</td>
      <td className="num">{article.shopifyCost !== null ? money(article.shopifyCost) : '—'}</td>
      <td>
        <form className="row-actions" onSubmit={(event) => void handleSubmit(event)}>
          <input
            type="text"
            inputMode="decimal"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={article.shopifyCost !== null ? 'Shopify' : 'à saisir'}
            aria-label={`Coût saisi pour ${article.title}`}
            aria-invalid={parsed === 'invalid'}
            style={{ width: 90 }}
          />
          <button type="submit" className="btn btn--small" disabled={isSaving || !dirty || parsed === 'invalid'}>
            Enregistrer
          </button>
          {error && <span className="login__error">{error}</span>}
        </form>
      </td>
      <td className="num">
        {effective !== null ? money(effective) : <strong>manquant</strong>}
        {derived && <span className="kpi__hint"> déduit : kit + piano</span>}
      </td>
    </tr>
  );
}

export function ArticleCostsEditor() {
  const articles = useArticleCosts();
  const write = useWriteArticleCost();
  const missing = (articles.data ?? []).filter(
    (article) => article.unitCost === null && article.shopifyCost === null && article.derivedCost === null && article.category !== 'subscription',
  );

  return (
    <Card
      title="Coût de revient des articles"
      subtitle={`€ HT par unité, par variante Shopify, vendus sur 12 mois. Le coût saisi ici prime sur le « coût par article » Shopify ; vide = Shopify. Une ligne de commande sans variante (supprimée dans Shopify) prend le coût du piano du même nom.${missing.length > 0 ? ` ${missing.length} article${missing.length > 1 ? 's' : ''} sans coût : la MC2 attend.` : ''}`}
      className="marker-form-card"
    >
      {articles.isError && (
        <p className="notice notice--error" role="alert">
          Articles illisibles : {articles.error.message}
        </p>
      )}
      {articles.data && articles.data.length === 0 ? (
        <p className="empty">Aucun article vendu sur les douze derniers mois.</p>
      ) : (
        <div className="table-wrap table-scroll" style={{ maxHeight: 420 }}>
          <table className="data">
            <thead>
              <tr>
                <th>Article</th>
                <th>Catégorie</th>
                <th className="num">Vendus (12 mois)</th>
                <th>Dernière vente</th>
                <th className="num">Coût Shopify</th>
                <th>Coût saisi</th>
                <th className="num">Coût retenu</th>
              </tr>
            </thead>
            <tbody>
              {(articles.data ?? []).map((article) => (
                <ArticleRow
                  key={article.variantId}
                  article={article}
                  isSaving={write.isPending}
                  onSave={async (unitCost) => {
                    await write.mutateAsync({ variantId: article.variantId, input: { unitCost } });
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
